import { notEmpty, theme, useNewsletterApi, useResponsive } from "@product/scmp-sdk";
import { useSyncedRef } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import type { NextPageContext, PageProps } from "next";
import { useCallback, useEffect, useState } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponentWithLayout } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import "swiper/css";
import "swiper/css/free-mode";

import { useKeywordMetaTags } from "shared/components/meta/hooks";
import { config } from "shared/data";
import { NEWSLETTER_BUNDLES } from "shared/data/newsletter";
import { accountAtom } from "shared/lib/account";

import { AppFooter } from "scmp-app/components/app-footer";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PageSeo } from "scmp-app/components/meta";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { onMessage, sendMessage } from "scmp-app/pages/newsletters/hooks";
import type { newslettersPageQuery } from "scmp-app/queries/__generated__/newslettersPageQuery.graphql";

import { NewsletterLandingContextProvider } from "./contexts";
import { useIframeCommunicator } from "./hooks";
import {
  Category,
  CategoryListContainer,
  CategorySwiper,
  CategorySwiperContainer,
  CategorySwiperSlide,
  Container,
  EmailAlertsText,
  Heading,
  NewsletterCategorySlideshowContainer,
  SignUpText,
  StyledBundledNewsletterCategorySlideshow,
  StyledNewsletterCategorySlideshow,
  SubHeading,
} from "./styles";
import type { NewsLetterSignUpButtonAction, NewsLetterUnsubscribePayload } from "./types";

const query = graphql`
  query newslettersPageQuery($bundledNewslettersEntityIds: [String!]) {
    newsletterCategories(first: 100, orderBy: { field: WEIGHT, direction: DESC }) {
      edges {
        node {
          entityId
          name
          ...newsletterCategoryItemSlideshowNewsletterCategory
        }
      }
    }
    ...bundledNewsletterCategoryItemSlideshowNewsletterCategory
      @arguments(bundledNewslettersEntityIds: $bundledNewslettersEntityIds)
  }
`;

const NewslettersPage: RelayComponentWithLayout<PageProps, newslettersPageQuery> = ({
  preloadedQuery,
  ...pageProps
}) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const isEmbed = pageProps.appInitializerConfiguration?.theme === "embed";
  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), false);
  const keywordTags = useKeywordMetaTags("newsletters");
  const { isLoggedIn } = useAtomValue(accountAtom);

  useIframeCommunicator(isEmbed);

  // Redirect logged-in users to accounts settings page, except in embed mode
  useEffect(() => {
    if (!isLoggedIn || isEmbed) return;

    const url = new URL(`${config.account.scmpAccountHost}/manage/newsletter`);
    window.location.href = url.toString();
  }, [isLoggedIn, isEmbed]);

  const handleCategoryClick = useCallback(
    (entityId: string) => {
      if (typeof window === "undefined") return;
      const target = document.querySelector(
        `#newsletterCategorySlideshow-${entityId}`,
      ) as HTMLElement;
      if (!notEmpty(target)) return;

      if (isEmbed) {
        sendMessage("SCROLL_REQUEST", JSON.stringify({ scrollTop: target.offsetTop + 20 }));
        return;
      }

      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },
    [isEmbed],
  );

  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  const [unsubscribePayload, setUnsubscribePayload] = useState<
    NewsLetterUnsubscribePayload | undefined
  >();
  const [isMountedListener, setIsMountedListener] = useState(false);
  const { handleUnsubscribeNewsletter } = useNewsletterApi();

  const newsletterHandler = useCallback(
    async (data: string) => {
      if (!isEmbed) return;
      const { type } = JSON.parse(data) as NewsLetterSignUpButtonAction;

      switch (type) {
        case "UNSUBSCRIBE_CANCELLED":
          break;
        case "UNSUBSCRIBE_CONFIRMED":
          if (!notEmpty(unsubscribePayload)) break;

          const { dataMatrix, email, newsletterIds } = unsubscribePayload;
          await handleUnsubscribeNewsletter(newsletterIds, email, dataMatrix);
          break;
      }
      setUnsubscribePayload(undefined);
    },
    [handleUnsubscribeNewsletter, isEmbed, unsubscribePayload],
  );

  const syncedNewsletterHandler = useSyncedRef(newsletterHandler);

  useEffect(() => {
    if (isMountedListener || !isEmbed) return;
    onMessage("UNSUBSCRIBE_RESPONSE", (data: string) => {
      void syncedNewsletterHandler.current(data);
    });
    setIsMountedListener(true);
  }, [isEmbed, isMountedListener, syncedNewsletterHandler]);

  return (
    <NewsletterLandingContextProvider
      isEmbed={isEmbed}
      setUnsubscribePayload={setUnsubscribePayload}
    >
      <PageSeo
        additionalMetaTags={[...keywordTags]}
        openGraph={{ title: "Newsletters | South China Morning Post" }}
        title="Newsletters"
      />
      <Container $isEmbed={isEmbed}>
        <Heading>
          <SignUpText>
            <span>Sign up</span> for our
          </SignUpText>
          <EmailAlertsText>Newsletters & Email Alerts</EmailAlertsText>
        </Heading>
        <SubHeading>
          The best of the South China Morning Post, delivered to your inbox for free
        </SubHeading>
        {isTabletUp ? (
          <CategoryListContainer>
            <Category onClick={() => handleCategoryClick("0")}>Newsletter Bundles</Category>
            {data?.newsletterCategories?.edges.map((edge, index) => (
              <Category key={index} onClick={() => handleCategoryClick(edge.node.entityId)}>
                {edge.node.name}
              </Category>
            ))}
          </CategoryListContainer>
        ) : (
          <CategorySwiperContainer>
            <CategorySwiper
              slidesOffsetAfter={10}
              slidesOffsetBefore={20}
              slidesPerView="auto"
              spaceBetween={8}
            >
              <CategorySwiperSlide>
                <Category onClick={() => handleCategoryClick("0")}>Newsletter Bundles</Category>
              </CategorySwiperSlide>
              {data?.newsletterCategories?.edges.map((edge, index) => (
                <CategorySwiperSlide key={index}>
                  <Category onClick={() => handleCategoryClick(edge.node.entityId)}>
                    {edge.node.name}
                  </Category>
                </CategorySwiperSlide>
              ))}
            </CategorySwiper>
          </CategorySwiperContainer>
        )}

        <NewsletterCategorySlideshowContainer>
          <StyledBundledNewsletterCategorySlideshow
            id="newsletterCategorySlideshow-0"
            reference={data}
          />
        </NewsletterCategorySlideshowContainer>

        {data?.newsletterCategories?.edges.map(edge => (
          <NewsletterCategorySlideshowContainer $isEmbed={isEmbed} key={edge.node.entityId}>
            <StyledNewsletterCategorySlideshow
              id={`newsletterCategorySlideshow-${edge.node.entityId}`}
              key={edge.node.entityId}
              reference={edge.node}
            />
          </NewsletterCategorySlideshowContainer>
        ))}
      </Container>

      {!isEmbed && <AppFooter />}
    </NewsletterLandingContextProvider>
  );
};

NewslettersPage.displayName = "NewslettersPage";

const getPageProps = (context: NextPageContext) => {
  const isEmbedMode = notEmpty(context.query.embed);

  const pageProps: PageProps = {
    appInitializerConfiguration: {
      theme: isEmbedMode ? "embed" : "default",
    },
  };

  if (isEmbedMode) {
    pageProps.pageConfiguration = {
      ...pageProps.pageConfiguration,
      responsivePadding: {
        desktopUp: 0,
        mobileUp: 0,
        tabletUp: 0,
      },
    };
    pageProps.contentConfiguration = {
      ...pageProps.contentConfiguration,
      responsivePadding: {
        desktopUp: 0,
        mobileUp: 0,
        tabletUp: 0,
      },
    };
  }
  return pageProps;
};

export default withRelay(NewslettersPage, query, {
  clientSideProps: context => getPageProps(context),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: context => Promise.resolve(getPageProps(context)),
  variablesFromContext: () => ({
    bundledNewslettersEntityIds: [
      ...new Set(Object.values(NEWSLETTER_BUNDLES).flatMap(({ newsletters }) => newsletters)),
    ],
  }),
});
